import { Form } from "@/components/form/form";
import { FormField } from "@/components/form/form-field/form-field";
import { FormControl } from "@/components/form/form-control";
import { FormLabel } from "@/components/form/form-label";
import { FormMessage } from "@/components/form/form-message";
import { Footer } from "@/components/footer";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useContactSubmitContactForm } from "@/orval/contact";
import type { ContactSubmissionRequestDto } from "@/orval/model";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Send } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_marketing/contact-us")({
  component: RouteComponent,
});

const defaultValues: ContactSubmissionRequestDto = {
  name: "",
  email: "",
  message: "",
};

function RouteComponent() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { mutateAsync: submitContact, isPending } = useContactSubmitContactForm();

  const handleSubmit = async (data: ContactSubmissionRequestDto) => {
    try {
      const result = await submitContact({ data });

      if (result.success) {
        setIsSubmitted(true);
      } else {
        toast.error(result.message);
      }
    } catch {
      toast.error("An error occurred while submitting your message. Please try again later.");
    }
  };

  if (isSubmitted) {
    return (
      <div className="flex min-h-screen flex-col">
        <PageHeader title="Contact Us" />
        <div className="container mx-auto px-4 py-12 max-w-2xl flex-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-center text-green-600">Thank You!</CardTitle>
              <CardDescription className="text-center">
                Your message has been sent successfully. We'll get back to you soon.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Button asChild variant="outline">
                <Link to="/">Return to Home</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <PageHeader title="Contact Us" />

      <div className="container mx-auto px-4 py-12 max-w-2xl flex-1">
        <Card>
          <CardHeader>
            <CardTitle>Get in Touch</CardTitle>
            <CardDescription>
              Have a question or want to learn more about Veronics? We'd love to hear from you. Send us a message and
              we'll respond as soon as possible.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form<ContactSubmissionRequestDto>
              useFormProps={{ defaultValues }}
              onSubmit={handleSubmit}
              className="gap-y-6"
            >
              <FormField
                name="name"
                label="Name"
                inputProps={{
                  placeholder: "Your full name",
                  disabled: isPending,
                }}
                rules={{ required: true }}
              />

              <FormField
                name="email"
                label="Email"
                inputProps={{
                  type: "email",
                  placeholder: "your.email@example.com",
                  disabled: isPending,
                }}
                rules={{ required: true }}
              />

              <FormField
                name="message"
                label="Message"
                render={({ field, label }) => (
                  <div className="flex flex-col gap-y-2">
                    <FormLabel>{label}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Tell us how we can help you..."
                        className="min-h-[120px]"
                        disabled={isPending}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </div>
                )}
                rules={{ required: true }}
              />

              {/* No privacy policy yet, so commenting out the privacy policy section */}
              {/* <div className="text-sm text-muted-foreground">
                <p>
                  By submitting this form, you agree to our privacy policy. We'll only use your information to respond
                  to your inquiry.
                </p>
              </div> */}

              <Button type="submit" disabled={isPending} className="w-full">
                {isPending ? (
                  <>Sending…</>
                ) : (
                  <>
                    <Send className="size-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </Form>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
}
